// src/components/layout/Header.js - FINAL FIXED VERSION
import React, { useState, useEffect, useCallback, useRef } from "react";
import { AppBar, Box, IconButton, Avatar, Button } from "@mui/material";
import { Menu, Receipt, Download, Link2 } from "lucide-react";
import Select from "react-select";
import { useAuth } from "../../contexts/AuthContext";
import { useGrade } from "../../contexts/GradeContext";
import { supabase } from "../../config/supabase";
import { useNavigate } from "react-router-dom";

const Header = ({ toggleSidebar }) => {
	const { user } = useAuth();
	const { selectedGrade, updateSelectedGrade } = useGrade();
	const navigate = useNavigate();
	const [grades, setGrades] = useState([]);
	const isTabVisibleRef = useRef(true); // Use ref instead of state

	// Memoize fetchTeacherGrades
	const fetchTeacherGrades = useCallback(async () => {
		if (user?.profile?.role === "ROLE_SCHOOL_TEACHER") {
			try {
				const { data: teacherGrades, error: gradesError } = await supabase
					.from("teacher_grades")
					.select(
						`
						grade_id,
						grades:grade_id (
							id,
							name
						)
					`
					)
					.eq("profile_id", user.profile.id);

				if (gradesError) {
					console.error("Error fetching teacher grades:", gradesError);
					return;
				}

				if (teacherGrades && teacherGrades.length > 0) {
					const formattedGrades = teacherGrades
						.filter((tg) => tg.grades)
						.map((tg) => ({
							value: tg.grades.id,
							label: tg.grades.name,
						}))
						.sort((a, b) => a.label.localeCompare(b.label));

					setGrades(formattedGrades);

					if (formattedGrades.length > 0 && !selectedGrade) {
						updateSelectedGrade(formattedGrades[0].value);
					} else if (
						selectedGrade &&
						!formattedGrades.find((g) => g.value === selectedGrade)
					) {
						if (formattedGrades.length > 0) {
							updateSelectedGrade(formattedGrades[0].value);
						} else {
							updateSelectedGrade(null);
						}
					}
				} else {
					setGrades([]);
					updateSelectedGrade(null);
				}
			} catch (error) {
				console.error("Error in fetchTeacherGrades:", error);
			}
		}
	}, [
		user?.profile?.id,
		user?.profile?.role,
		selectedGrade,
		updateSelectedGrade,
	]);

	// Track tab visibility using ref to avoid re-renders
	useEffect(() => {
		const handleVisibilityChange = () => {
			isTabVisibleRef.current = !document.hidden;
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () =>
			document.removeEventListener("visibilitychange", handleVisibilityChange);
	}, []);

	// Set up real-time listener and initial fetch
	useEffect(() => {
		if (user?.profile?.id && user?.profile?.role === "ROLE_SCHOOL_TEACHER") {
			// Initial fetch
			fetchTeacherGrades();

			// Set up subscription
			const channelName = `teacher_grades_changes_${user.profile.id}`;
			const teacherGradesSubscription = supabase
				.channel(channelName)
				.on(
					"postgres_changes",
					{
						event: "*",
						schema: "public",
						table: "teacher_grades",
						filter: `profile_id=eq.${user.profile.id}`,
					},
					(payload) => {
						// Use ref value instead of state to avoid dependency issues
						if (isTabVisibleRef.current && payload.eventType !== "SELECT") {
							fetchTeacherGrades();
						}
					}
				)
				.subscribe();

			// Cleanup subscription
			return () => {
				if (teacherGradesSubscription) {
					supabase.removeChannel(teacherGradesSubscription);
				}
			};
		}
	}, [user?.profile?.id, user?.profile?.role, fetchTeacherGrades]); // Removed isTabVisible from dependencies

	const handleGradeChange = (option) => {
		updateSelectedGrade(option.value);
	};

	const currentSelectedOption = grades.find(
		(grade) => grade.value === selectedGrade
	);

	const customStyles = {
		container: (provided) => ({
			...provided,
			width: "100%",
		}),
		control: (provided, state) => ({
			...provided,
			minHeight: "32px",
			height: "32px",
			backgroundColor: "#f3f4f6",
			borderColor: state.isFocused ? "#6366f1" : "#e5e7eb",
			boxShadow: "none",
			"&:hover": {
				borderColor: "#6366f1",
			},
		}),
		valueContainer: (provided) => ({
			...provided,
			height: "32px",
			padding: "0 8px",
		}),
		input: (provided) => ({
			...provided,
			margin: "0",
			padding: "0",
		}),
		singleValue: (provided) => ({
			...provided,
			margin: "0",
			color: "#374151",
			fontSize: "0.875rem",
		}),
		indicatorSeparator: () => ({
			display: "none",
		}),
		dropdownIndicator: (provided) => ({
			...provided,
			padding: "4px 8px",
			"& svg": {
				width: "16px",
				height: "16px",
			},
		}),
		menu: (provided) => ({
			...provided,
			marginTop: "4px",
			zIndex: 9999,
			backgroundColor: "white",
			boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
			border: "1px solid #e5e7eb",
		}),
		menuList: (provided) => ({
			...provided,
			padding: "4px",
		}),
		option: (provided, state) => ({
			...provided,
			backgroundColor: state.isSelected
				? "#6366f1"
				: state.isFocused
				? "#f3f4f6"
				: "white",
			color: state.isSelected ? "white" : "#374151",
			fontSize: "0.875rem",
			cursor: "pointer",
			"&:active": {
				backgroundColor: "#6366f1",
			},
			padding: "8px 12px",
		}),
	};

	const isTeacher = user?.profile?.role === "ROLE_SCHOOL_TEACHER";
	const canAccessPortalResources =
		!isTeacher &&
		["ROLE_FUNMIYO_ADMIN", "ROLE_FUNMATH_ADMIN", "ROLE_FUNMIYO_EMPLOYEE", "ROLE_VIDEO_EDITOR"].includes(
			user?.profile?.role
		);

	return (
		<AppBar
			position="fixed"
			sx={{
				backgroundColor: "white",
				boxShadow: "none",
				borderBottom: "1px solid #e5e7eb",
				height: "48px",
				ml: { xs: 0, md: "200px" },
				width: { xs: "100%", md: "calc(100% - 200px)" },
			}}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					height: "100%",
					px: 2,
				}}>
				{/* Logo for mobile view */}
				<Box
					sx={{
						display: { xs: "flex", md: "none" },
						alignItems: "center",
					}}
					onClick={() => navigate("/dashboard")}>
					<img
						src="/logo.png"
						alt="Funmiyo Portal"
						style={{
							height: "24px",
							width: "auto",
							objectFit: "contain",
						}}
					/>
				</Box>

				{/* Right-side items container */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: { xs: 1, sm: 2 },
						marginLeft: "auto",
					}}>
					{isTeacher && grades.length > 0 && (
						<Box sx={{ width: { xs: "150px", sm: "200px" } }}>
							<Select
								value={currentSelectedOption}
								onChange={handleGradeChange}
								options={grades}
								styles={customStyles}
								isSearchable={false}
								placeholder="Select Grade"
								menuPortalTarget={document.body}
								menuPosition="fixed"
							/>
						</Box>
					)}

					{canAccessPortalResources && (
						<Button
							startIcon={<Download size={18} />}
							onClick={() => navigate("/downloads")}
							sx={{
								height: "32px",
								backgroundColor: "#f3f4f6",
								color: "#374151",
								textTransform: "none",
								fontSize: "0.875rem",
								fontWeight: 500,
								px: 2,
								display: { xs: "none", sm: "flex" },
								"&:hover": {
									backgroundColor: "#e5e7eb",
								},
							}}>
							Downloads
						</Button>
					)}

					{canAccessPortalResources && (
						<Button
							startIcon={<Link2 size={18} />}
							onClick={() => navigate("/central-tracker")}
							sx={{
								height: "32px",
								backgroundColor: "#f3f4f6",
								color: "#374151",
								textTransform: "none",
								fontSize: "0.875rem",
								fontWeight: 500,
								px: 2,
								display: { xs: "none", sm: "flex" },
								"&:hover": {
									backgroundColor: "#e5e7eb",
								},
							}}>
							Central Tracker
						</Button>
					)}

					{!isTeacher && (
						<Button
							startIcon={<Receipt size={18} />}
							onClick={() => navigate("/submit-expense")}
							sx={{
								height: "32px",
								backgroundColor: "#f3f4f6",
								color: "#374151",
								textTransform: "none",
								fontSize: "0.875rem",
								fontWeight: 500,
								px: 2,
								display: { xs: "none", sm: "flex" },
								"&:hover": {
									backgroundColor: "#e5e7eb",
								},
							}}>
							Submit Expense
						</Button>
					)}

					<IconButton
						onClick={toggleSidebar}
						sx={{
							display: { xs: "flex", md: "none" },
							color: "#6b7280",
						}}>
						<Menu size={20} />
					</IconButton>

					<Avatar
						sx={{
							width: 32,
							height: 32,
							fontSize: "14px",
							bgcolor: "#6366f1",
							cursor: "pointer",
						}}>
						{user?.email?.charAt(0).toUpperCase()}
					</Avatar>
				</Box>
			</Box>
		</AppBar>
	);
};

export default Header;
